import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Globe, ArrowLeft, Cookie, Settings, BarChart3, Shield, Megaphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Link } from "react-router-dom";
import { DEFAULT_CONSENT, getConsent, setConsent } from "../lib/consent";

const cookiesContent = {
  es: {
    title: "Política de Cookies",
    backHome: "Volver al inicio",
    intro: "Esta página utiliza cookies para mejorar tu experiencia de navegación. A continuación te explicamos qué son, qué tipos utilizamos y cómo puedes gestionarlas.",
    sections: [
      {
        title: "¿Qué son las cookies?",
        content: `Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (ordenador, tablet o móvil) cuando visitas un sitio web. Permiten que el sitio recuerde tus acciones y preferencias durante un período de tiempo.

Las cookies no son virus ni programas maliciosos. Son herramientas estándar utilizadas por prácticamente todos los sitios web.`
      },
      {
        title: "Tipos de cookies que utilizamos",
        content: ""
      },
      {
        title: "¿Cómo gestionar las cookies?",
        content: `Puedes configurar tu navegador para que rechace todas las cookies o para que te avise cuando se envía una cookie. Sin embargo, si rechazas las cookies, es posible que algunas funciones del sitio web no funcionen correctamente.

Para gestionar las cookies en los navegadores más comunes:
• Chrome: Configuración > Privacidad y seguridad > Cookies
• Firefox: Opciones > Privacidad y seguridad
• Safari: Preferencias > Privacidad
• Edge: Configuración > Cookies y permisos del sitio`
      },
      {
        title: "Actualizaciones de esta política",
        content: `Podemos actualizar esta política de cookies periódicamente. Te recomendamos revisar esta página regularmente para estar informado sobre cómo utilizamos las cookies.

Si tienes preguntas sobre nuestra política de cookies, puedes contactarnos en: adm@globaltechxperts.nl`
      }
    ],
    cookieTypes: [
      {
        icon: Shield,
        name: "Cookies esenciales",
        description: "Necesarias para el funcionamiento básico del sitio. No se pueden desactivar.",
        required: true,
        key: "essential"
      },
      {
        icon: BarChart3,
        name: "Cookies analíticas",
        description: "Nos ayudan a entender cómo los visitantes interactúan con el sitio (Google Analytics).",
        required: false,
        key: "analytics"
      },
      {
        icon: Settings,
        name: "Cookies de preferencias",
        description: "Permiten recordar tus preferencias como el idioma seleccionado.",
        required: false,
        key: "preferences"
      },
      {
        icon: Megaphone,
        name: "Cookies publicitarias",
        description: "Miden el rendimiento de nuestras campañas (píxel de TikTok). Solo se activan con tu consentimiento y no enviamos datos personales.",
        required: false,
        key: "marketing"
      }
    ],
    savePreferences: "Guardar preferencias",
    acceptAll: "Aceptar todas",
    lastUpdate: "Última actualización: Febrero 2026"
  },
  en: {
    title: "Cookie Policy",
    backHome: "Back to home",
    intro: "This page uses cookies to improve your browsing experience. Below we explain what they are, what types we use and how you can manage them.",
    sections: [
      {
        title: "What are cookies?",
        content: `Cookies are small text files that are stored on your device (computer, tablet or mobile) when you visit a website. They allow the site to remember your actions and preferences over a period of time.

Cookies are not viruses or malicious programs. They are standard tools used by virtually all websites.`
      },
      {
        title: "Types of cookies we use",
        content: ""
      },
      {
        title: "How to manage cookies?",
        content: `You can configure your browser to reject all cookies or to alert you when a cookie is sent. However, if you reject cookies, some website functions may not work properly.

To manage cookies in the most common browsers:
• Chrome: Settings > Privacy and security > Cookies
• Firefox: Options > Privacy and security
• Safari: Preferences > Privacy
• Edge: Settings > Cookies and site permissions`
      },
      {
        title: "Updates to this policy",
        content: `We may update this cookie policy periodically. We recommend reviewing this page regularly to stay informed about how we use cookies.

If you have questions about our cookie policy, you can contact us at: adm@globaltechxperts.nl`
      }
    ],
    cookieTypes: [
      {
        icon: Shield,
        name: "Essential cookies",
        description: "Necessary for basic site functionality. Cannot be disabled.",
        required: true,
        key: "essential"
      },
      {
        icon: BarChart3,
        name: "Analytics cookies",
        description: "Help us understand how visitors interact with the site (Google Analytics).",
        required: false,
        key: "analytics"
      },
      {
        icon: Settings,
        name: "Preference cookies",
        description: "Allow remembering your preferences such as selected language.",
        required: false,
        key: "preferences"
      },
      {
        icon: Megaphone,
        name: "Advertising cookies",
        description: "Measure the performance of our campaigns (TikTok pixel). Only activated with your consent; no personal data is sent.",
        required: false,
        key: "marketing"
      }
    ],
    savePreferences: "Save preferences",
    acceptAll: "Accept all",
    lastUpdate: "Last updated: February 2026"
  }
};

export default function CookiesPolicyPage() {
  const { language } = useLanguage();
  const content = cookiesContent[language];
  
  const [cookiePreferences, setCookiePreferences] = useState(() => getConsent() || DEFAULT_CONSENT);

  const handleToggle = (key) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAcceptAll = () => {
    setCookiePreferences(DEFAULT_CONSENT);
  };

  const handleSavePreferences = () => {
    setConsent(cookiePreferences);
    alert(language === 'es' ? '¡Preferencias guardadas!' : 'Preferences saved!');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="glass border-b border-neutral-800/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-lg">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight text-white font-['Outfit']">
                Global Tech Xpert
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full border-neutral-700 text-neutral-300 hover:bg-neutral-800 gap-2">
                <ArrowLeft className="w-4 h-4" />
                {content.backHome}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
            <Cookie className="w-7 h-7 text-orange-400" />
          </div>
          <h1 className="text-4xl font-bold text-white font-['Outfit']">{content.title}</h1>
        </div>
        
        <p className="text-neutral-400 text-lg mb-12">{content.intro}</p>
        
        <div className="space-y-8">
          {/* First section */}
          <div className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">{content.sections[0].title}</h2>
            <p className="text-neutral-400 whitespace-pre-line">{content.sections[0].content}</p>
          </div>

          {/* Cookie types section */}
          <div className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">{content.sections[1].title}</h2>
            
            <div className="space-y-4">
              {content.cookieTypes.map((cookie) => (
                <div 
                  key={cookie.key}
                  className="flex items-start gap-4 p-4 rounded-xl bg-neutral-800/50 border border-neutral-700"
                >
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <cookie.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-white">{cookie.name}</h3>
                      <Switch 
                        checked={cookiePreferences[cookie.key]}
                        onCheckedChange={() => handleToggle(cookie.key)}
                        disabled={cookie.required}
                        data-testid={`cookie-toggle-${cookie.key}`}
                      />
                    </div>
                    <p className="text-sm text-neutral-400">{cookie.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 mt-6">
              <Button 
                onClick={handleSavePreferences}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6"
                data-testid="save-cookie-preferences"
              >
                {content.savePreferences}
              </Button>
              <Button 
                onClick={handleAcceptAll}
                variant="outline"
                className="rounded-full border-neutral-700 text-neutral-300 hover:bg-neutral-800 px-6"
                data-testid="accept-all-cookies"
              >
                {content.acceptAll}
              </Button>
            </div>
          </div>

          {/* Remaining sections */}
          {content.sections.slice(2).map((section) => (
            <div key={section.title} className="glass rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">{section.title}</h2>
              <p className="text-neutral-400 whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </div>

        <p className="text-neutral-500 text-sm mt-8">{content.lastUpdate}</p>
      </main>

      {/* Footer */}
      <footer className="border-t border-neutral-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-neutral-500 text-sm">
          © {new Date().getFullYear()} Global Tech Xpert. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
}
